import React, { useState, useEffect } from 'react';
import { Search, ChevronLeft, ChevronRight, X, ChevronDown, Download, Eye } from 'lucide-react';
import { getWalkin, getWalkinById } from '../../../../Services/allApi';

const Walkin = () => {
  const [walkins, setWalkins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedWalkin, setSelectedWalkin] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showItemsDropdown, setShowItemsDropdown] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedWalkinDetails, setSelectedWalkinDetails] = useState(null);
  
  const itemsPerPageOptions = [5, 10, 25, 50, 100];

  useEffect(() => {
    // Fetch walk-ins when component mounts or when dependencies change
    fetchWalkins();
  }, [currentPage, itemsPerPage, searchTerm]);

  const fetchWalkins = async () => {
    setLoading(true);
    try {
      // Build query parameters for pagination and search
      const params = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      // Update the API call to include search params 
      const response = await getWalkin(params.toString());
      
      if (response.error) {
        throw new Error(response.message || 'Failed to load walk-in data');
      }
      
      // Process the response based on the new API response structure
      let walkinsArray = [];
      let totalPagesCount = 1;
      
      if (response && response.data.results && Array.isArray(response.data.results)) {
        walkinsArray = response.data.results;
        // If count is provided, calculate total pages
        if (response.count) {
          totalPagesCount = Math.ceil(response.count / itemsPerPage);
        } else {
          totalPagesCount = Math.ceil(walkinsArray.length / itemsPerPage);
        }
      } else if (Array.isArray(response)) {
        walkinsArray = response;
        totalPagesCount = Math.ceil(response.length / itemsPerPage);
      } else {
        console.error('Unexpected API response format:', response);
        throw new Error('Unexpected data format from API');
      }
      
      setWalkins(walkinsArray);
      setTotalPages(totalPagesCount);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching walkins:', err);
      setError(err.message || "Failed to load walk-in data. Please try again.");
      setWalkins([]); // Ensure walkins is an empty array if there's an error
      setLoading(false);
    }
  };

  const fetchWalkinDetails = async (id) => {
    try {
      const response = await getWalkinById(id);
      
      if (response.error) {
        throw new Error(response.message || 'Failed to load walk-in details');
      }
      
      setSelectedWalkinDetails(response.data);
      setShowModal(true);
    } catch (err) {
      console.error('Error fetching walkin details:', err);
      setError(err.message || "Failed to load walk-in details. Please try again.");
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchWalkins();
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
    setShowItemsDropdown(false);
  };

  const openWalkinDetails = (walkin) => {
    setSelectedWalkin(walkin);
    fetchWalkinDetails(walkin.id);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedWalkin(null);
    setSelectedWalkinDetails(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (e) {
      console.error('Error formatting date:', e);
      return "Invalid Date";
    }
  };

  // Function to export walkin data (mock functionality)
  const exportToPDF = () => {
    setExportLoading(true);
    
    // Simulate export process
    setTimeout(() => {
      setExportLoading(false);
      alert("Export functionality would generate a PDF in a real implementation");
    }, 1500);
  };

  // Status badge color mapping based on course interest
  const getCourseColor = (course) => {
    if (!course) return "bg-gray-100 text-gray-800";
    
    if (course.includes("Python")) {
      return "bg-blue-100 text-blue-800";
    } else if (course.includes("Java")) {
      return "bg-orange-100 text-orange-800";
    } else if (course.includes("MEA(R)N")) {
      return "bg-green-100 text-green-800";
    } else if (course.includes("Data Science")) {
      return "bg-purple-100 text-purple-800";
    } else {
      return "bg-gray-100 text-gray-800";
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showItemsDropdown && !event.target.closest('.items-dropdown-container')) {
        setShowItemsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showItemsDropdown]);

  // Safe rendering check for walkins
  const hasWalkins = Array.isArray(walkins) && walkins.length > 0;

  if (loading && !hasWalkins) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg font-medium text-gray-600">Loading walk-in data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-700 p-4 rounded-md">
        <p className="font-medium">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 md:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Walk-in Management</h1>
          {/* {hasWalkins && (
            <button
              onClick={exportToPDF}
              disabled={exportLoading}
              className="mt-2 sm:mt-0 flex items-center px-3 py-1 text-xs sm:text-sm bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
            >
              {exportLoading ? (
                <span>Exporting...</span>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-1" />
                  <span>Export PDF</span>
                </>
              )}
            </button>
          )} */}
        </div>
        <form onSubmit={handleSearch} className="relative w-full sm:w-64 md:w-80">
          <div className="relative flex items-center">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <Search className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
            <input
              type="text"
              placeholder="Search walk-ins..."
              className="w-full pl-10 pr-20 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button
              type="submit"
              className="absolute right-0 h-full px-3 sm:px-4 bg-purple-600 text-white text-sm rounded-r-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
            >
              Search
            </button>
          </div>
        </form>
      </div>

      {!hasWalkins ? (
        <div className="text-center py-8">
          <p className="text-gray-500 text-lg">No walk-ins found.</p>
        </div>
      ) : (
        <>
          {/* Mobile view: Card layout */}
          <div className="md:hidden space-y-4">
            {walkins.map((walkin) => (
              <div 
                key={walkin.id || `walkin-${Math.random()}`}
                className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 cursor-pointer hover:bg-gray-50"
              >
                <div className="font-medium text-gray-900 mb-1">
                  {walkin.name || 'No name'}
                </div>
                <div className="text-sm text-gray-600 mb-1">{walkin.phone_number || 'No phone'}</div>
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-500">
                    <span className="inline-block mr-2">{walkin.qualification || 'No qualification'}</span>
                  </div>
                  <span className={`px-2 py-1 text-xs leading-5 font-semibold rounded-full ${getCourseColor(walkin.course_interested)}`}>
                    {walkin.course_interested?.split('-')[0] || 'Unknown'}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop view: Table layout */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-3 px-2 lg:px-4 text-left text-xs lg:text-sm font-medium text-gray-600 uppercase tracking-wider">Name</th>
                  <th className="py-3 px-2 lg:px-4 text-left text-xs lg:text-sm font-medium text-gray-600 uppercase tracking-wider">Phone</th>
                  <th className="py-3 px-2 lg:px-4 text-left text-xs lg:text-sm font-medium text-gray-600 uppercase tracking-wider">Prefered Location</th>

                  <th className="py-3 px-2 lg:px-4 text-left text-xs lg:text-sm font-medium text-gray-600 uppercase tracking-wider">Passout</th>
                  <th className="py-3 px-2 lg:px-4 text-left text-xs lg:text-sm font-medium text-gray-600 uppercase tracking-wider">Course</th>
                  <th className="py-3 px-2 lg:px-4 text-left text-xs lg:text-sm font-medium text-gray-600 uppercase tracking-wider">Date</th>
                  <th className="py-3 px-2 lg:px-4 text-left text-xs lg:text-sm font-medium text-gray-600 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {walkins.map((walkin) => (
                  <tr 
                    key={walkin.id || `walkin-${Math.random()}`} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => openWalkinDetails(walkin)}
                  >
                    <td className="px-2 lg:px-4 py-3 lg:py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900 text-sm lg:text-base">
                        {walkin.name || 'N/A'}
                      </div>
                    </td>
                    <td className="px-2 lg:px-4 py-3 lg:py-4 whitespace-nowrap text-xs lg:text-sm text-gray-600">
                      {walkin.phone_number || 'N/A'}
                    </td>
                    <td className="px-2 lg:px-4 py-3 lg:py-4 whitespace-nowrap text-xs lg:text-sm text-gray-600">
                      {walkin.preferred_location || 'N/A'}
                    </td>

                    <td className="px-2 lg:px-4 py-3 lg:py-4 whitespace-nowrap text-xs lg:text-sm text-gray-600">
                      {walkin.passout_year || 'N/A'}
                    </td>
                    <td className="px-2 lg:px-4 py-3 lg:py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getCourseColor(walkin.course_interested)}`}>
                        {walkin.course_interested?.split('-')[0] || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-2 lg:px-4 py-3 lg:py-4 whitespace-nowrap text-xs lg:text-sm text-gray-600">
                      {formatDate(walkin.created_at)}
                    </td>
                    <td className="px-2 lg:px-4 py-3 lg:py-4 whitespace-nowrap text-xs lg:text-sm text-green-600" onClick={() => openWalkinDetails(walkin)}
                    >
                      <Eye/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-6 gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <div className="text-xs sm:text-sm text-gray-600">
                Showing page {currentPage} of {totalPages || 1}
              </div>
              <div className="relative items-dropdown-container">
                <div 
                  className="flex items-center space-x-1 px-2 py-1 sm:px-3 sm:py-2 border border-gray-300 rounded-md cursor-pointer text-xs sm:text-sm"
                  onClick={() => setShowItemsDropdown(!showItemsDropdown)}
                >
                  <span className="text-gray-700">{itemsPerPage} per page</span>
                  <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />
                </div>
                {showItemsDropdown && (
                  <div className="absolute z-10 mt-1 w-28 sm:w-36 bg-white shadow-lg rounded-md py-1 border border-gray-200">
                    {itemsPerPageOptions.map((option) => (
                      <div 
                        key={option} 
                        className="px-4 py-1 sm:py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                        onClick={() => handleItemsPerPageChange(option)}
                      >
                        {option} per page
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex space-x-2 text-xs sm:text-sm">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`flex items-center px-2 py-1 sm:px-3 sm:py-1 rounded ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`flex items-center px-2 py-1 sm:px-3 sm:py-1 rounded ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Walk-in Details Modal */}
      {showModal && (selectedWalkinDetails || selectedWalkin) && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 transition-opacity" 
              onClick={closeModal}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full mx-4 sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-start">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">
                    Walk-in Details
                  </h3>
                  <button
                    onClick={closeModal}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <X className="h-5 w-5 sm:h-6 sm:w-6" />
                  </button>
                </div>
                {/* Use selectedWalkinDetails from API or fallback to selectedWalkin */}
                {(() => {
                  const walkin = selectedWalkinDetails || selectedWalkin;
                  return (
                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500">Name</label>
                        <div className="mt-1 text-sm sm:text-base text-gray-900">{walkin.name || 'N/A'}</div>
                      </div>
                      
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500">Phone Number</label>
                        <div className="mt-1 text-sm sm:text-base text-gray-900">{walkin.phone_number || 'N/A'}</div>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-500">Qualification</label>
                          <div className="mt-1 text-sm sm:text-base text-gray-900">{walkin.qualification || 'N/A'}</div>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-500">Passout Year</label>
                          <div className="mt-1 text-sm sm:text-base text-gray-900">{walkin.passout_year || 'N/A'}</div>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500">College Name</label>
                        <div className="mt-1 text-sm sm:text-base text-gray-900">{walkin.college_name || 'N/A'}</div>
                      </div>
                      {walkin.preferred_location? <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500">Preferred Llocation</label>
                        <div className="mt-1 text-sm sm:text-base text-gray-900">{walkin.preferred_location || 'N/A'}</div>
                      </div>
                      :
                      ''
                      }
                      
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500">Course Interested</label>
                        <div className="mt-1">
                          <span className={`px-2 py-1 text-xs leading-5 font-semibold rounded-full ${getCourseColor(walkin.course_interested)}`}>
                            {walkin.course_interested || 'Unknown'}
                          </span>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500">Created Date</label>
                        <div className="mt-1 text-sm sm:text-base text-gray-900">{formatDate(walkin.created_at)}</div>
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-purple-600 text-base font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Walkin;